"use client";

import React from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import ExportedImage from "next-image-export-optimizer";
import {
  Mail,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Instagram,
  Youtube,
} from "lucide-react";
import { brand } from "@/config/brand";
import { getActiveBrandId } from "@/config/brands";
import { useTranslations } from "@/lib/i18n-client";
import { isPlaceholderValue } from "@/lib/contact";
import TrustpilotBadge from "@/components/shared/TrustpilotBadge";
import AppStoreWidget from "@/components/shared/AppStoreWidget";

const navigation = [
  { key: "nav.home", fallback: "Home", href: "/" },
  { key: "nav.destinations", fallback: "Destinations", href: "/destinations/" },
  { key: "nav.plans", fallback: "Plans", href: "/#pricing" },
  { key: "nav.compatibility", fallback: "Compatibility", href: "/#compatibility" },
  { key: "nav.installationGuide", fallback: "Installation Guide", href: "/installation-guide/" },
  { key: "nav.aboutUs", fallback: "About Us", href: "/about-us/" },
  { key: "nav.contactUs", fallback: "Contact Us", href: "/contact-us/" },
  { key: "nav.blog", fallback: "Blog", href: "/blog/" },
];

const handleLinkClick = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

const supportLinks = [
  // { name: 'Try 30 Days', href: '/try-30-days' },
  { key: "footer.support.topup", fallback: "Top up an existing eSIM", href: "/top-up/" },
  { key: "footer.support.faq", fallback: "FAQ", href: "/faq/" },
  { key: "footer.support.terms", fallback: "Terms of Service", href: "/terms/" },
  { key: "footer.support.privacy", fallback: "Privacy Policy", href: "/privacy/" },
  { key: "footer.support.cookie", fallback: "Cookie Policy", href: "/cookie-policy/" },
  { key: "footer.support.refund", fallback: "Refund Policy", href: "/refund-policy/" },
];

const paymentMethods = [
  { key: "footer.payments.visa", fallback: "Visa" },
  { key: "footer.payments.mastercard", fallback: "Mastercard" },
  { key: "footer.payments.applePay", fallback: "Apple Pay" },
  { key: "footer.payments.googlePay", fallback: "Google Pay" },
];

const socialLinks = [
  { name: "Facebook", icon: Facebook, href: brand.social.facebook },
  { name: "Twitter", icon: Twitter, href: brand.social.twitter },
  { name: "Instagram", icon: Instagram, href: brand.social.instagram },
  { name: "YouTube", icon: Youtube, href: brand.social.youtube },
];

export default function Footer() {
  const { t } = useTranslations();
  const crispChrome =
    getActiveBrandId() !== "esimsupra" && getActiveBrandId() !== "aquaglass-global-connect";
  const visibleSocialLinks = socialLinks.filter((social) => !isPlaceholderValue(social.href));
  const hasPhoneContact = !isPlaceholderValue(brand.contact.phone);

  return (
    <footer className="relative border-t border-border/50">
      {!crispChrome && <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/40 via-black/15 to-transparent dark:from-black/60" />}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-2 mb-6">
              {/* <Tv className="h-8 w-8 text-primary" /> */}
              <Link
                href="/"
                className="flex items-center space-x-2 transition-smooth hover:scale-105"
                onClick={handleLinkClick}
              >
                <ExportedImage
                  src={brand.brand.logo}
                  alt={brand.brand.logoAlt}
                  width={160}
                  height={40}
                  className="h-8 w-auto"
                />
              </Link>
            </div>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              {t("footer.tagline", `${brand.brand.tagline}. ${brand.brand.description}`)}
            </p>
            <div className="mb-8">
              <TrustpilotBadge 
                className="opacity-80 hover:opacity-100 transition-opacity" 
              />
            </div>
            {visibleSocialLinks.length > 0 && (
              <div className="flex space-x-4">
                {visibleSocialLinks.map((social) => {
                const IconComponent = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={crispChrome ? "w-10 h-10 rounded-lg border border-border bg-card flex items-center justify-center hover:bg-primary/20 transition-smooth" : "w-10 h-10 rounded-lg glass flex items-center justify-center hover:bg-primary/20 transition-smooth"}
                    aria-label={social.name}
                  >
                    <IconComponent className="w-5 h-5" />
                  </a>
                );
                })}
              </div>
            )}
            <div className="mt-8">
              <AppStoreWidget theme="auto" layout="vertical" />
            </div>
          </div>

          {/* Navigation Links */}
          <div>
            <h3 className="font-semibold text-foreground mb-6">{t("footer.navigation", "Navigation")}</h3>
            <ul className="space-y-3">
              {navigation.slice(0, 4).map((link) => (
                <li key={link.key}>
                  <Link
                    href={link.href}
                    onClick={handleLinkClick}
                    className="text-muted-foreground hover:text-primary transition-smooth"
                  >
                    {t(link.key, link.fallback)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* More Links */}
          <div>
            <h3 className="font-semibold text-foreground mb-6">{t("footer.more", "More")}</h3>
            <ul className="space-y-3">
              {navigation.slice(4).map((link) => (
                <li key={link.key}>
                  <Link
                    href={link.href}
                    onClick={handleLinkClick}
                    className="text-muted-foreground hover:text-primary transition-smooth"
                  >
                    {t(link.key, link.fallback)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support & Legal */}
          <div>
            <h3 className="font-semibold text-foreground mb-6">
              {t("footer.support.title", "Support & Legal")}
            </h3>
            <ul className="space-y-3">
              {supportLinks.map((link) => (
                <li key={link.key}>
                  <Link
                    href={link.href}
                    onClick={handleLinkClick}
                    className="text-muted-foreground hover:text-primary transition-smooth"
                  >
                    {t(link.key, link.fallback)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-semibold text-foreground mb-6">{t("footer.contact.title", "Get in Touch")}</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-primary" />
                <button
                  onClick={() => {
                    const email = "contact" + "@" + "esimsupra.com";
                    window.location.href = `mailto:${email}`;
                  }}
                  className="text-muted-foreground hover:text-primary transition-smooth text-sm text-left"
                >
                  {brand.contact.email.split('@')[0]} [at] {brand.contact.email.split('@')[1]}
                </button>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-primary" />
                {hasPhoneContact ? (
                  <a
                    href={`tel:${brand.contact.phone}`}
                    className="text-muted-foreground hover:text-primary transition-smooth text-sm"
                  >
                    {brand.contact.phone}
                  </a>
                ) : (
                  <span className="text-muted-foreground text-sm">
                    {t("footer.contact.chatOnly", "24/7 chat support")}
                  </span>
                )}
              </div>
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-primary mt-0.5" />
                <span className="text-muted-foreground text-sm">
                  {brand.contact.address}
                  <br />
                  {brand.contact.supportHours}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="border-t border-border/50 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div>
              <h4 className="font-semibold text-foreground mb-3">
                {t("footer.payments.title", "Secure Payment Methods")}
              </h4>
              <div className="flex items-center gap-6 text-muted-foreground">
                {paymentMethods.map((method) => (
                  <span key={method.key} className="font-semibold">
                    {t(method.key, method.fallback)}
                  </span>
                ))}
              </div>
            </div>
            <div className="text-center md:text-right">
              <div className={crispChrome ? "border border-border bg-card rounded-lg px-4 py-2 mb-2" : "glass rounded-lg px-4 py-2 mb-2"}>
                <span className="text-sm text-emerald-700 dark:text-emerald-400">{t("footer.security.ssl", "🔒 SSL Secured")}</span>
              </div>
              <div className="text-sm text-muted-foreground">
                {t("footer.security.encryption", "256-bit encryption")}
              </div>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-border/50 mt-12 pt-8 text-center">
          <p className="text-muted-foreground">{brand.legal.copyrightText}</p>
        </div>
      </div>
    </footer>
  );
}
